'use client';

import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/providers/i18n';

type MultiAudioPickerProps = {
  urls: string[];
  activeUrl: string;
  onChangeActive: (url: string) => void;
  disabled?: boolean;
  className?: string;
};

export function MultiAudioPicker({ urls, activeUrl, onChangeActive, disabled, className }: MultiAudioPickerProps) {
  const { t } = useI18n();
  const safeUrls = useMemo(() => (Array.isArray(urls) ? urls.filter(Boolean) : []), [urls]);
  const activeIndex = useMemo(() => Math.max(0, safeUrls.indexOf(activeUrl)), [safeUrls, activeUrl]);

  if (safeUrls.length <= 1) return null;

  return (
    <div className={cn('flex items-center justify-center gap-1.5', className)}>
      {safeUrls.map((u, i) => {
        const selected = i === activeIndex;
        return (
          <button
            key={`${u}-${i}`}
            type="button"
            disabled={disabled}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onChangeActive(u);
            }}
            onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
            className={cn(
              'inline-flex items-center justify-center rounded-full text-[11px] font-semibold tabular-nums transition-all',
              'h-6 min-w-6 px-1.5',
              selected
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted/60 text-muted-foreground hover:bg-muted',
              disabled && 'opacity-50 pointer-events-none',
            )}
            title={t('Track {{n}}', { n: i + 1 })}
          >
            {i + 1}
          </button>
        );
      })}
    </div>
  );
}
